import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { supabase } from "../supabase";
import { C, MOIS, mono } from "../config/theme";
import { fmt, fmtK } from "../utils/format";
import KpiCard from "../components/KpiCard";
import FilterBar from "../components/FilterBar";
import MiniTable from "../components/MiniTable";
import DrillDown from "../components/DrillDown";
import StatusMessage from "../components/StatusMessage";
import CustomTooltip from "../components/CustomTooltip";

/* ─── Palette donuts ─── */
const COLORS = [
  "#F59E0B", "#3B82F6", "#16A34A", "#8B5CF6", "#EC4899",
  "#0EA5E9", "#F97316", "#6366F1", "#14B8A6", "#EF4444",
];

/* ─── Labels nature dépense ─── */
const NATURE_LABELS = {
  mo:             "Main d'œuvre",
  fourniture:     "Fourniture",
  sous_traitance: "Sous-traitance",
  location:       "Location matériel",
  stock:          "Stock",
  interim:        "Intérim",
  divers:         "Divers",
};

/* ─── Couleurs statut ─── */
const STATUT_COLORS = {
  "étude":   { bg: "#EFF6FF", color: C.blue,   border: C.blue },
  "travaux": { bg: "#FFFBEB", color: C.accent, border: C.accent },
  "achevé":  { bg: C.greenBg,  color: C.green,  border: C.green },
};

function StatutBadge({ statut }) {
  const v = STATUT_COLORS[statut] || { bg: C.bg, color: C.slate, border: C.border };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      background: v.bg, color: v.color, border: `1px solid ${v.border}40`, whiteSpace: "nowrap",
    }}>
      {statut}
    </span>
  );
}

/* ─── Titre de section ─── */
function SectionTitle({ children, color = C.navy }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
      color, margin: "24px 0 12px", paddingBottom: 8, borderBottom: `2px solid ${color}20`,
    }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE DIRECTION — Dashboard consolidé tous chantiers
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageDirection() {
  /* ─── Filtres ─── */
  const [annee, setAnnee] = useState(null);
  const [filConducteur, setFilConducteur] = useState("");
  const [filActivite, setFilActivite] = useState("");
  const [filAgence, setFilAgence] = useState("");

  /* ─── Données filtres (dropdowns) — réutilise bv_rpc_ca_filtres ─── */
  const [filtresData, setFiltresData] = useState(null);

  /* ─── Données RPCs ─── */
  const [global, setGlobal] = useState(null);
  const [syntheseChantiers, setSyntheseChantiers] = useState([]);
  const [perfEquipe, setPerfEquipe] = useState({ par_conducteur: [], par_charge_affaires: [] });
  const [perfAxes, setPerfAxes] = useState({ par_activite: [], par_agence: [] });
  const [risque, setRisque] = useState({ en_derive: [], marge_negative: [] });
  const [graphiques, setGraphiques] = useState(null);

  /* ─── États ─── */
  const [loadingFiltres, setLoadingFiltres] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Paramètres dérivés ─── */
  const paramsCommon = useMemo(() => ({
    ...(filActivite   ? { p_activite_id: filActivite }     : {}),
    ...(filAgence     ? { p_agence_id: filAgence }         : {}),
    ...(filConducteur ? { p_conducteur_id: filConducteur } : {}),
  }), [filActivite, filAgence, filConducteur]);

  const paramsWithAnnee = useMemo(() => ({
    p_annee: annee,
    ...paramsCommon,
  }), [annee, paramsCommon]);

  /* ═══════════════════════════════════════════════════════════════════════════
     CHARGEMENT INITIAL — Filtres
     ═══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    async function loadFiltres() {
      setLoadingFiltres(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_ca_filtres");
      if (err) {
        console.error("Erreur filtres direction:", err);
        setError("Impossible de charger les filtres.");
        setLoadingFiltres(false);
        return;
      }
      setFiltresData(data);
      if (data?.annees?.length > 0) setAnnee(data.annees[0]);
      setLoadingFiltres(false);
    }
    loadFiltres();
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════════
     CHARGEMENT DONNÉES
     ═══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!annee) return;

    async function loadData() {
      setLoadingData(true);
      try {
        const [r1, r2, r3, r4, r5, r6] = await Promise.all([
          supabase.rpc("bv_rpc_direction_global", paramsWithAnnee),
          supabase.rpc("bv_rpc_direction_synthese_chantiers", paramsCommon),
          supabase.rpc("bv_rpc_direction_performance_equipe", {
            ...(filActivite ? { p_activite_id: filActivite } : {}),
            ...(filAgence ? { p_agence_id: filAgence } : {}),
          }),
          supabase.rpc("bv_rpc_direction_performance_axes", {
            ...(filConducteur ? { p_conducteur_id: filConducteur } : {}),
          }),
          supabase.rpc("bv_rpc_direction_chantiers_risque", paramsCommon),
          supabase.rpc("bv_rpc_direction_graphiques", paramsWithAnnee),
        ]);

        if (r1.data) setGlobal(r1.data);
        if (r2.data) setSyntheseChantiers(r2.data || []);
        if (r3.data) setPerfEquipe(r3.data);
        if (r4.data) setPerfAxes(r4.data);
        if (r5.data) setRisque(r5.data);
        if (r6.data) setGraphiques(r6.data);

        [r1, r2, r3, r4, r5, r6].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC direction #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur chargement direction:", err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoadingData(false);
      }
    }

    loadData();
  }, [annee, paramsCommon, paramsWithAnnee, filActivite, filAgence, filConducteur]);

  /* ═══════════════════════════════════════════════════════════════════════════
     DONNÉES DÉRIVÉES POUR LES GRAPHIQUES
     ═══════════════════════════════════════════════════════════════════════════ */

  /* Donut marché par activité — avec % */
  const donutMarche = useMemo(() => {
    const rows = graphiques?.marche_par_activite || [];
    const total = rows.reduce((a, r) => a + (r.montant_ht || 0), 0);
    return rows.map((r) => ({
      ...r,
      name: r.activite,
      value: r.montant_ht || 0,
      pct: total > 0 ? ((r.montant_ht / total) * 100).toFixed(1) : "0.0",
    }));
  }, [graphiques]);

  /* Donut dépenses par nature */
  const donutDepenses = useMemo(() => {
    const obj = graphiques?.depenses_par_nature || {};
    const rows = Object.entries(obj)
      .filter(([, v]) => (v || 0) > 0)
      .map(([k, v]) => ({ key: k, name: NATURE_LABELS[k] || k, value: v }));
    const total = rows.reduce((a, r) => a + r.value, 0);
    return rows.map((r) => ({
      ...r,
      pct: total > 0 ? ((r.value / total) * 100).toFixed(1) : "0.0",
    }));
  }, [graphiques]);

  /* Courbe marge mensuelle — compléter les 12 mois */
  const chartMargeMois = useMemo(() => {
    const map = new Map();
    (graphiques?.marge_mensuelle || []).forEach((r) => map.set(r.mois, r.marge_brute || 0));
    const out = [];
    for (let m = 1; m <= 12; m++) {
      out.push({ mois: MOIS[m], marge: map.get(m) || 0 });
    }
    return out;
  }, [graphiques]);

  /* ═══════════════════════════════════════════════════════════════════════════
     COLONNES DRILL-DOWN — Synthèse chantiers (8 colonnes essentielles)
     Code, conducteur, activité, agence, alertes : accessibles via Pilotage chantier
     et Alertes. Ici on garde une vue stratégique « marges + cash ».
     ═══════════════════════════════════════════════════════════════════════════ */
  const colonnesChantiers = useMemo(() => [
    { key: "libelle", label: "Chantier", bold: true },
    { key: "client", label: "Client" },
    { key: "statut", label: "Statut", render: (r) => <StatutBadge statut={r.statut} /> },
    { key: "marche_ht", label: "Marché HT", align: "right", mono: true, render: (r) => fmt(r.marche_ht) },
    { key: "facture_ht", label: "Facturé HT", align: "right", mono: true, render: (r) => fmt(r.facture_ht) },
    {
      key: "raf", label: "RAF", align: "right", mono: true, bold: true,
      render: (r) => fmt(r.raf),
      color: (r) => (r.raf < 0 ? C.red : C.text),
    },
    {
      key: "avancement_pct", label: "Avancement", align: "right", mono: true,
      render: (r) => `${Number(r.avancement_pct || 0).toFixed(1)}%`,
    },
    {
      key: "taux_marge", label: "Marge %", align: "right", mono: true, bold: true,
      render: (r) => `${Number(r.taux_marge || 0).toFixed(1)}%`,
      color: (r) => (Number(r.taux_marge || 0) < 0 ? C.red : Number(r.taux_marge || 0) < 15 ? C.accent : C.green),
    },
  ], []);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */

  if (loadingFiltres) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }

  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }

  if (!filtresData?.annees?.length) {
    return <StatusMessage type="empty" message="Aucune donnée disponible." detail="Les données n'ont pas encore été synchronisées depuis l'ERP." />;
  }

  if (!annee) return null;

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Direction</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Vue consolidée — performance, risques et trajectoire de l'exercice
        </p>
      </div>

      {/* ─── Filtres ─── */}
      <FilterBar
        annees={filtresData.annees} annee={annee} setAnnee={setAnnee}
        showPeriode={false}
        showClient={false}
        showChargeAffaires={false}
        conducteurs={filtresData.conducteurs || []} filConducteur={filConducteur} setFilConducteur={setFilConducteur}
        activites={filtresData.activites || []} filActivite={filActivite} setFilActivite={setFilActivite}
        agences={filtresData.agences || []} filAgence={filAgence} setFilAgence={setFilAgence}
      />

      {loadingData && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation des données…
        </div>
      )}

      {/* ═══ KPI LIGNE 1 — Volumétrie chantiers + financière ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
        <KpiCard label="Chantiers travaux" value={global?.nb_travaux || 0} color={C.accent} isCurrency={false} />
        <KpiCard label="Chantiers étude" value={global?.nb_etude || 0} color={C.blue} isCurrency={false} />
        <KpiCard label="Chantiers achevés" value={global?.nb_acheves || 0} color={C.green} isCurrency={false} />
        <KpiCard label="Marché total (HT)" value={global?.marche_total || 0} color={C.navy} />
        <KpiCard label={`CA YTD ${annee}`} value={global?.ca_ytd || 0} color={C.accent} />
      </div>

      {/* ═══ KPI LIGNE 2 — RAF, marges, alertes ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Reste à facturer" value={global?.raf_global || 0} color="#8B5CF6" />
        <KpiCard label="Marge brute" value={global?.marge_brute || 0} color={C.blue} />
        <KpiCard label="Taux marge brute" value={global?.taux_marge_brute || 0} color={C.blue} suffix="%" />
        <KpiCard
          label="Chantiers en alerte"
          value={global?.nb_chantiers_alerte || 0}
          color={(global?.nb_chantiers_alerte || 0) > 0 ? C.accent : C.slateLight}
          isCurrency={false}
        />
        <KpiCard
          label="Alertes critiques"
          value={global?.nb_alertes_critiques || 0}
          color={(global?.nb_alertes_critiques || 0) > 0 ? C.red : C.slateLight}
          isCurrency={false}
        />
      </div>

      {/* ═══ SYNTHÈSE TOUS CHANTIERS ═══ */}
      <SectionTitle color={C.accent}>Synthèse — tous les chantiers</SectionTitle>
      {syntheseChantiers.length > 0 ? (
        <DrillDown
          title={`Liste des chantiers (${syntheseChantiers.length})`}
          color={C.accent}
          columns={colonnesChantiers}
          rows={syntheseChantiers}
        />
      ) : (
        <StatusMessage type="empty" message="Aucun chantier ne correspond aux filtres sélectionnés." />
      )}

      {/* ═══ PERFORMANCE ÉQUIPE — conducteur + chargé d'affaires ═══ */}
      <SectionTitle color={C.blue}>Performance équipe</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable
          title="Par conducteur de travaux"
          color={C.blue}
          headers={[
            { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
            { key: "conducteur", label: "Conducteur", bold: true, maxW: 180, total: () => "Total" },
            { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chantiers || 0), 0) },
            { key: "marche_total", label: "Marché", align: "right", mono: true, bold: true,
              render: (r) => fmtK(r.marche_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.marche_total || 0), 0)) },
            { key: "facture_total", label: "Facturé", align: "right", mono: true,
              render: (r) => fmtK(r.facture_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.facture_total || 0), 0)) },
            { key: "taux_marge", label: "Marge %", align: "right", mono: true,
              render: (r) => `${Number(r.taux_marge || 0).toFixed(1)}%`,
              total: () => "" },
            { key: "nb_alertes", label: "Alertes", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_alertes || 0), 0) },
          ]}
          rows={perfEquipe.par_conducteur || []}
        />

        <MiniTable
          title="Par chargé d'affaires"
          color="#F97316"
          headers={[
            { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
            { key: "charge_affaires", label: "Chargé d'affaires", bold: true, maxW: 200, total: () => "Total" },
            { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chantiers || 0), 0) },
            { key: "marche_total", label: "Marché", align: "right", mono: true, bold: true,
              render: (r) => fmtK(r.marche_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.marche_total || 0), 0)) },
            { key: "facture_total", label: "Facturé", align: "right", mono: true,
              render: (r) => fmtK(r.facture_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.facture_total || 0), 0)) },
            { key: "taux_marge", label: "Marge %", align: "right", mono: true,
              render: (r) => `${Number(r.taux_marge || 0).toFixed(1)}%`,
              total: () => "" },
          ]}
          rows={perfEquipe.par_charge_affaires || []}
        />
      </div>

      {/* ═══ PERFORMANCE AXES — activité + agence ═══ */}
      <SectionTitle color="#8B5CF6">Performance par axe</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable
          title="Par activité"
          color="#8B5CF6"
          headers={[
            { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
            { key: "code", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "activite", label: "Activité", maxW: 200, total: () => "Total" },
            { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chantiers || 0), 0) },
            { key: "marche_total", label: "Marché", align: "right", mono: true, bold: true,
              render: (r) => fmtK(r.marche_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.marche_total || 0), 0)) },
            { key: "facture_total", label: "Facturé", align: "right", mono: true,
              render: (r) => fmtK(r.facture_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.facture_total || 0), 0)) },
          ]}
          rows={perfAxes.par_activite || []}
        />

        <MiniTable
          title="Par agence"
          color="#14B8A6"
          headers={[
            { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
            { key: "code", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "agence", label: "Agence", maxW: 200, total: () => "Total" },
            { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chantiers || 0), 0) },
            { key: "marche_total", label: "Marché", align: "right", mono: true, bold: true,
              render: (r) => fmtK(r.marche_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.marche_total || 0), 0)) },
            { key: "facture_total", label: "Facturé", align: "right", mono: true,
              render: (r) => fmtK(r.facture_total),
              total: (rows) => fmtK(rows.reduce((a, r) => a + (r.facture_total || 0), 0)) },
          ]}
          rows={perfAxes.par_agence || []}
        />
      </div>

      {/* ═══ RISQUES — dérive + marge négative ═══ */}
      <SectionTitle color={C.red}>Chantiers à risque</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        {/* En dérive */}
        {(risque.en_derive || []).length > 0 ? (
          <MiniTable
            title={`En dérive — dépenses > budget à avancement (${risque.en_derive.length})`}
            color={C.red}
            headers={[
              { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
              { key: "libelle", label: "Chantier", maxW: 200, total: () => "Total" },
              { key: "conducteur", label: "Conducteur", maxW: 140, total: () => "" },
              { key: "avancement_pct", label: "Avanc.", align: "right", mono: true,
                render: (r) => `${Number(r.avancement_pct || 0).toFixed(1)}%`, total: () => "" },
              { key: "budget_avancement", label: "Budget av.", align: "right", mono: true,
                render: (r) => fmtK(r.budget_avancement),
                total: (rows) => fmtK(rows.reduce((a, r) => a + (r.budget_avancement || 0), 0)) },
              { key: "depenses_reelles", label: "Dépenses", align: "right", mono: true,
                render: (r) => fmtK(r.depenses_reelles),
                total: (rows) => fmtK(rows.reduce((a, r) => a + (r.depenses_reelles || 0), 0)) },
              { key: "ecart", label: "Écart", align: "right", mono: true, bold: true,
                render: (r) => <span style={{ color: C.red }}>+{fmtK(r.ecart)}</span>,
                total: (rows) => <span style={{ color: C.red }}>+{fmtK(rows.reduce((a, r) => a + (r.ecart || 0), 0))}</span> },
            ]}
            rows={risque.en_derive}
          />
        ) : (
          <StatusMessage type="info" message="Aucun chantier en dérive." detail="Les dépenses sont conformes au budget à avancement." />
        )}

        {/* Marge négative */}
        {(risque.marge_negative || []).length > 0 ? (
          <MiniTable
            title={`Marge négative (${risque.marge_negative.length})`}
            color={C.red}
            headers={[
              { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
              { key: "libelle", label: "Chantier", maxW: 200, total: () => "Total" },
              { key: "conducteur", label: "Conducteur", maxW: 140, total: () => "" },
              { key: "marche_ht", label: "Marché", align: "right", mono: true,
                render: (r) => r.marche_ht != null ? fmtK(r.marche_ht) : "—", total: () => "" },
              { key: "marge_brute", label: "Marge brute", align: "right", mono: true, bold: true,
                render: (r) => r.marge_brute != null ? <span style={{ color: C.red }}>{fmt(r.marge_brute)}</span> : "—",
                total: () => "" },
              { key: "taux_marge", label: "Taux", align: "right", mono: true,
                render: (r) => r.taux_marge != null ? <span style={{ color: C.red }}>{Number(r.taux_marge).toFixed(1)}%</span> : "—",
                total: () => "" },
            ]}
            rows={risque.marge_negative}
          />
        ) : (
          <StatusMessage type="info" message="Aucun chantier en marge négative." detail="Tous les chantiers conservent une marge brute positive." />
        )}
      </div>

      {/* ═══ GRAPHIQUES — donut activité + courbe marge + donut dépenses ═══ */}
      <SectionTitle color={C.blue}>Vision graphique</SectionTitle>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        {/* Donut marché par activité */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#8B5CF6", marginBottom: 8 }}>
            Marché par activité
          </div>
          {donutMarche.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={donutMarche} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={60} strokeWidth={2} stroke={C.surface}>
                    {donutMarche.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, fontSize: 11 }}>
                {donutMarche.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                    </div>
                    <span style={{ fontFamily: mono, fontWeight: 600, color: C.slate, whiteSpace: "nowrap" }}>{item.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: "center", color: C.slateLight, fontSize: 12 }}>Aucune donnée</div>
          )}
        </div>

        {/* Donut dépenses par nature */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.red, marginBottom: 8 }}>
            Dépenses par nature (YTD)
          </div>
          {donutDepenses.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={donutDepenses} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={60} strokeWidth={2} stroke={C.surface}>
                    {donutDepenses.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, fontSize: 11 }}>
                {donutDepenses.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                    </div>
                    <span style={{ fontFamily: mono, fontWeight: 600, color: C.slate, whiteSpace: "nowrap" }}>{item.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: 24, textAlign: "center", color: C.slateLight, fontSize: 12 }}>Aucune dépense enregistrée</div>
          )}
        </div>
      </div>

      {/* Courbe marge mensuelle */}
      <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)", marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.green, marginBottom: 4 }}>
          Marge brute mensuelle — {annee}
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.slate }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: C.green }} /> Marge brute
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartMargeMois}>
            <XAxis dataKey="mois" fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="marge" name="Marge brute" stroke={C.green} strokeWidth={2.5} dot={{ r: 4, fill: C.green }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

